import { VulnerableUsersReport } from '@/client/reports';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { DataTable, DataTableColumnType } from '@/components/tables/crud';
import { isDemoModeActive } from '@/utils/demo-mode';
import {
  DataTablePageEvent,
  DataTableRowClickEvent,
} from 'primereact/datatable';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { default as styled } from 'styled-components';

const StyledHeader = styled.h2<{ isMainDashboard?: boolean }>`
  font-size: var(--heading-small-font-size);
  line-height: var(--heading-small-line-height);
  margin-block: ${({ isMainDashboard }) =>
    isMainDashboard ? 'var(--big-padding) 0' : 'var(--xsmall-padding) 0'};
`;

const StyledDescription = styled.p`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: var(--xsmall-padding) 0 var(--default-padding) 0;
`;

const StyledTable = styled(DataTable)`
  &.p-datatable {
    border: 1px solid var(--gray-medium);
    border-radius: var(--xsmall-border-radius);

    th[role='columnheader'] {
      color: var(--black-main) !important;
      background-color: var(--white-main) !important;
      .p-column-title {
        min-width: max-content;
      }
    }

    tr {
      &:nth-child(even) {
        background-color: var(--white-main) !important;

        &:hover {
          background-color: var(--white-main) !important;
        }
      }

      &:nth-child(odd) {
        background-color: var(--beige-main) !important;

        &:hover {
          background-color: var(--beige-main) !important;
        }
      }
    }

    td[role='cell'] {
      padding: var(--small-padding) var(--medium-padding) !important;
    }

    .p-datatable-thead tr th {
      &:first-child {
        border-top-left-radius: var(--xsmall-border-radius);
      }
      &:last-child {
        border-top-right-radius: var(--xsmall-border-radius);
      }
    }
  }
`;

type VulnerableUsersListProps = {
  isLoading: boolean;
  isFetching: boolean;
  vulnerableUsers?: VulnerableUsersReport[];
  onSort: (event: DataTablePageEvent) => void;
  sort: [string, 'asc' | 'desc'] | null;
  rows?: number;
  skip?: number;
  onPage?: (event: DataTablePageEvent) => void;
  total?: number | undefined;
  isMainDashboard?: boolean;
};

export const VulnerableUsersList = ({
  vulnerableUsers,
  isLoading,
  isFetching,
  onSort,
  sort,
  rows,
  skip,
  onPage,
  total,
  isMainDashboard,
}: VulnerableUsersListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('generic.name'),
      sortable: false,
      filterable: false,
      render: (row: VulnerableUsersReport) => row?.user?.name,
    },
    {
      field: 'department',
      header: t('generic.department'),
      sortable: false,
      filterable: false,
      render: (row: VulnerableUsersReport) => row?.user?.department || '—',
    },
    {
      field: 'sentCount',
      header: t('phishing.simulation.vulnerable.emailSent'),
      sortable: true,
      filterable: false,
      render: (row: VulnerableUsersReport) => row?.sentCount,
    },
    {
      field: 'clickedCount',
      header: t('phishing.simulation.vulnerable.emailLinkClicks'),
      sortable: true,
      filterable: false,
      render: (row: VulnerableUsersReport) => row?.clickedCount,
    },
    {
      field: 'submittedCount',
      header: t('phishing.simulation.vulnerable.submittedData'),
      sortable: true,
      filterable: false,
      render: (row: VulnerableUsersReport) => row?.submittedCount,
    },
  ];

  const onRowClick = (e: DataTableRowClickEvent) => {
    if (isLoading) return;
    navigate(
      RedirectPaths[RedirectPathsEnum.USERS_PHISHING](e?.data?.user?.id),
    );
  };

  const hasNoVulnerableUsers =
    !isLoading &&
    (total === 0 ||
      (total == null && (!vulnerableUsers || vulnerableUsers.length === 0)));

  return (
    <>
      <StyledHeader isMainDashboard={isMainDashboard}>
        {t('phishing.simulation.usersOverview.list')}
      </StyledHeader>
      {isMainDashboard && (
        <StyledDescription>
          {t('phishing.simulation.usersOverview.description')}
        </StyledDescription>
      )}
      {hasNoVulnerableUsers ? (
        <ChartEmptyState
          minHeight={510}
          type={ChartEmptyStateType.PHISHING}
          title={t('dashboard.phishing.vulnerable.empty.title')}
          description={t('dashboard.phishing.vulnerable.empty.description')}
        />
      ) : (
        <StyledTable
          id="vulnerable-users-list"
          dataKey="user.id"
          data={vulnerableUsers}
          count={total}
          isLoading={isLoading}
          isFetching={isFetching}
          columns={columns}
          onSort={onSort}
          sort={sort}
          rows={rows}
          skip={skip}
          onRowClick={isDemoModeActive() ? undefined : onRowClick}
          onPage={onPage}
        />
      )}
    </>
  );
};
